import DashCard from "../../../../../components/dash-card/dash-card.jsx";
import SentryEchart from "../../../../../components/echarts/sentry-echart.jsx";
import useGraphViewportWidth from "../../../../../hooks/use-graph-viewport-width.js";
import {fluidPx} from "../../../../../utils/responsive-graph.js";
import {useMemo} from "react";
import {translate} from "../../../../../components/translate/translate.jsx";

export default function TopNaturesCard({ natures }) {
    const vw = useGraphViewportWidth();
    const topNaturesSourceItems = natures?.topNatures;

    const topNaturesChart = useMemo(() => {
        const topNatures = Array.isArray(topNaturesSourceItems)
            ? topNaturesSourceItems.map(item => ({
                label: item.label ?? "",
                value: item.qty ?? 0,
            }))
            : [];
        const labelFs = fluidPx(12, 21, vw);
        const valueFs = fluidPx(14, 20, vw);
        const max = Math.max(...topNatures.map(item => item.value), 1);
        const values = Array.from({length: 5}, (_, index) => topNatures[index]?.value ?? 0);
        const y = Array.from({length: 5}, (_, index) => topNatures[index]?.label ?? "");

        return {
            grid: {
                top: labelFs + 14,
                left: 10,
                right: "13%",
                bottom: 4,
                outerBounds: {
                    top: labelFs + 14,
                    left: 10,
                    right: "13%",
                    bottom: 4,
                },
                outerBoundsContain: "axisLabel"
            },
            tooltip: {
                show: true,
                appendToBody: true,
                confine: false,
                extraCssText: "max-width: 320px; white-space: normal;"
            },
            xAxis: {
                type: "value",
                max,
                show: false,
            },
            yAxis: {
                type: "category",
                data: y,
                inverse: true,
                axisLabel: {show: false},
                axisLine: {show: false},
                axisTick: {show: false}
            },
            series: [
                {
                    type: "bar",
                    barWidth: "35%",
                    barCategoryGap: "24%",
                    itemStyle: {
                        color: {
                            type: "linear",
                            x: 0,
                            y: 0,
                            x2: 1,
                            y2: 0,
                            colorStops: [
                                { offset: 0, color: "#003878" },
                                { offset: 0.28, color: "#0080ED" },
                                { offset: 0.52, color: "#1CEAE6" },
                                { offset: 0.72, color: "#3E98D9" },
                                { offset: 1, color: "#003878" }
                            ]
                        },
                        borderRadius: 20
                    },
                    label: {
                        show: true,
                        position: [0, -4],
                        align: "left",
                        verticalAlign: "bottom",
                        color: "#AAAAAA",
                        fontSize: labelFs,
                        fontWeight: 400,
                        formatter: (params) => y[params.dataIndex]
                    },
                    showBackground: true,
                    backgroundStyle: {
                        color: "#3C3C4A",
                        borderRadius: 12
                    },
                    data: values
                },
                {
                    type: "bar",
                    barWidth: "35%",
                    barCategoryGap: "24%",
                    barGap: "-100%",
                    silent: true,
                    tooltip: { show: false },
                    itemStyle: {
                        color: "transparent"
                    },
                    label: {
                        show: true,
                        position: "right",
                        distance: 5,
                        color: "#FFFFFF",
                        fontSize: valueFs,
                        fontWeight: 600,
                        formatter: (params) =>
                            y[params.dataIndex] === ""
                                ? ""
                                : values[params.dataIndex]
                    },
                    data: values.map(() => max)
                }
            ]
        }
    }, [topNaturesSourceItems, vw]);

    const topClassificationsSourceItems = natures?.topClassifications;
    const topClassification = useMemo(() => Array.isArray(topClassificationsSourceItems)
        ? topClassificationsSourceItems.slice(0, 5).map(item => ({
            label: item.label ?? "",
            value: Number(item.qty) || 0,
        }))
        : [], [topClassificationsSourceItems]);

    const classificationChart = useMemo(() => {
        const chartItems = Array.from({length: 5}, (_, index) => topClassification[index] ?? {
            label: "",
            value: 0,
        });
        const values = chartItems.map(item => item.value);
        const maxValue = Math.max(...values, 1);

        return {
            grid: {
                top: 28,
                left: 0,
                right: 0,
                bottom: 26,
                outerBounds: {
                    top: 28,
                    left: 0,
                    right: 0,
                    bottom: 26,
                },
                outerBoundsContain: "axisLabel"
            },

            tooltip: {
                trigger: "axis",
                axisPointer: {
                    type: "shadow"
                },
                formatter: params => {
                    const coloredBar = params.find(item => item.seriesName === "Quantidade");

                    if (!coloredBar) return "";

                    const item = chartItems[coloredBar.dataIndex];
                    if (!item?.label) return "";

                    return `${translate(item.label)}: <b>${item.value}</b>`;
                }
            },

            xAxis: {
                type: "category",
                boundaryGap: true,
                data: chartItems.map((_, index) => `${index + 1}º`),

                axisLabel: {
                    color: "#FDFCFF",
                    fontSize: 14
                },

                axisLine: {
                    show: true,
                    lineStyle: {
                        color: "#555762"
                    }
                },

                axisTick: {
                    show: false
                }
            },

            yAxis: {
                type: "value",
                max: maxValue * 1.1,

                axisLabel: {
                    show: false,
                },

                axisTick: {
                    show: false,
                },

                splitLine: {
                    show: false,
                },

                axisLine: {
                    show: true,
                    lineStyle: {
                        color: "#555762",
                        width: 1,
                    },
                },
            },
            series: [
                {
                    name: "Fundo",
                    type: "bar",
                    barWidth: "68%",
                    barGap: "-100%",
                    silent: true,

                    itemStyle: {
                        color: "#4B4B5C",
                        borderRadius: [4, 4, 0, 0]
                    },

                    label: {
                        show: true,
                        position: "top",
                        distance: 8,
                        color: "#FDFCFF",
                        fontSize: 12,
                        fontWeight: "bold",
                        formatter: params => chartItems[params.dataIndex]?.label
                            ? values[params.dataIndex]
                            : ""
                    },

                    data: chartItems.map(() => maxValue),

                    z: 1
                },

                {
                    name: "Quantidade",
                    type: "bar",

                    barWidth: "68%",

                    itemStyle: {
                        borderRadius: [4, 4, 0, 0],

                        color: {
                            type: "linear",
                            x: 0,
                            y: 1,
                            x2: 1,
                            y2: 0,

                            colorStops: [
                                { offset: 0, color: "#003878" },
                                { offset: 0.5, color: "#0080ED" },
                                { offset: 1, color: "#1CEAE6" }
                            ]
                        }
                    },

                    data: values,

                    z: 2
                }
            ]
        };
    }, [topClassification]);

    return (
        <DashCard width="100%" height="100%" className="h-full min-h-0 col-span-23">
            <section className="h-full min-h-0 grid grid-cols-24 gap-(--dash-pad-md)">
                <article className="col-span-12 min-w-0 min-h-0 flex flex-col gap-2">
                    <h3 className="shrink-0 px-4 text-center text-(length:--font-md)! text-[#AAAAAA]">
                        Natureza dos alarmes - Top 5
                    </h3>
                    <SentryEchart
                        className="min-h-0"
                        option={topNaturesChart}
                    />
                </article>
                <article className="col-span-12 min-w-0 min-h-0 flex flex-col">
                    <h3 className="shrink-0 text-center text-(length:--font-md)! text-[#AAAAAA]">
                        Classificação na ocorrência - Top 5
                    </h3>
                    <article className="translate-y-3 flex-1 min-h-0 grid grid-cols-[minmax(0,1fr)_28%] gap-2 px-[20px]">
                        <SentryEchart
                            className="flex-1 min-h-0"
                            option={classificationChart}
                        />
                        <section className="self-end justify-self-start flex w-fit flex-col items-start pl-2 pb-10">
                            <h3 className="mb-2! text-xs font-bold text-[#AAAAAA]">
                                Veículo:
                            </h3>
                            <ol className="space-y-2 text-xs">
                                {topClassification.length > 0 ? (
                                    topClassification.map((item, index) => (
                                        <li
                                            key={`${item.label}-${index}`}
                                            className="flex min-w-0 gap-1"
                                        >
                                    <span className="shrink-0 text-[#E8E8E8]">
                                        {index + 1}º
                                    </span>
                                            <span className="truncate text-[#AAAAAA]">
                                        {translate(item.label)}
                                    </span>
                                        </li>
                                    ))
                                ) : (
                                    <li className="text-[#AAAAAA]">Sem dados</li>
                                )}
                            </ol>
                        </section>
                    </article>
                </article>
            </section>
        </DashCard>
    )
}
